import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import AnimatedKpiCard from '../common/AnimatedKpiCard';
import ChartCard from '../charts/ChartCard';
import { LineChart, BarChart, DoughnutChart, EmptyChart } from '../charts/AnalyticsCharts';
import { AnalyticsToolbar, AnalyticsSettingsModal, AnalyticsInfoModal, useAnalyticsControls } from './AnalyticsControls';

const ENQUIRY_PRESETS = [
  { value: 'today', label: 'Today vs Yesterday' },
  { value: 'week', label: 'This Week vs Last Week' },
  { value: 'month', label: 'This Month vs Last Month' },
  { value: 'year', label: 'This Year vs Last Year' },
];

const formatDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (value) => {
  const d = new Date(value);
  d.setHours(23, 59, 59, 999);
  return d;
};

const shiftDate = (value, amount, unit) => {
  const d = new Date(value);
  if (unit === 'month') d.setMonth(d.getMonth() + amount);
  else if (unit === 'year') d.setFullYear(d.getFullYear() + amount);
  else d.setDate(d.getDate() + amount);
  return d;
};

const buildPreviousQueryParams = (queryParams = {}) => {
  const preset = String(queryParams.preset || '12m');
  const groupBy = queryParams.groupBy || 'month';

  if (preset === 'today') {
    const today = startOfDay(new Date());
    const previous = shiftDate(today, -1, 'day');
    return { preset: 'custom', groupBy, from: formatDate(previous), to: formatDate(previous) };
  }

  if (preset === 'week') {
    const today = endOfDay(new Date());
    const currentStart = startOfDay(today);
    currentStart.setDate(currentStart.getDate() - currentStart.getDay());
    return {
      preset: 'custom',
      groupBy,
      from: formatDate(shiftDate(currentStart, -7, 'day')),
      to: formatDate(shiftDate(today, -7, 'day')),
    };
  }

  if (preset === '7d' || preset === '30d') {
    const days = preset === '7d' ? 7 : 30;
    const currentEnd = endOfDay(new Date());
    const currentStart = startOfDay(shiftDate(currentEnd, -(days - 1), 'day'));
    const previousEnd = endOfDay(shiftDate(currentStart, -1, 'day'));
    const previousStart = startOfDay(shiftDate(previousEnd, -(days - 1), 'day'));
    return { preset: 'custom', groupBy, from: formatDate(previousStart), to: formatDate(previousEnd) };
  }

  if (preset === 'month') {
    const now = new Date();
    const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthLastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    const prevMonthEnd = endOfDay(new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      Math.min(now.getDate(), prevMonthLastDay),
    ));
    return { preset: 'custom', groupBy, from: formatDate(prevMonthStart), to: formatDate(prevMonthEnd) };
  }

  if (preset === 'year') {
    const now = new Date();
    return {
      preset: 'custom',
      groupBy,
      from: `${now.getFullYear() - 1}-01-01`,
      to: formatDate(shiftDate(endOfDay(now), -1, 'year')),
    };
  }

  if (preset === '12m') {
    const now = new Date();
    const currentStart = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const previousStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 12, 1);
    const previousEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
    return { preset: 'custom', groupBy, from: formatDate(previousStart), to: formatDate(previousEnd) };
  }

  if (queryParams.from || queryParams.to) {
    const currentFrom = startOfDay(queryParams.from || queryParams.to || new Date());
    const currentTo = endOfDay(queryParams.to || queryParams.from || new Date());
    const diffMs = currentTo.getTime() - currentFrom.getTime();
    const previousTo = endOfDay(shiftDate(currentFrom, -1, 'day'));
    const previousFrom = startOfDay(new Date(previousTo.getTime() - diffMs));
    return { preset: 'custom', groupBy, from: formatDate(previousFrom), to: formatDate(previousTo) };
  }

  return { preset: '30d', groupBy };
};

const buildComparisonMeta = (currentMeta = {}, previousMeta = {}) => ({
  presentLabel: currentMeta.periodLabel || 'Present period',
  previousLabel: previousMeta.periodLabel || 'Previous period',
});

export default function EnquiriesAnalyticsV2() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [showCoursePicker, setShowCoursePicker] = useState(false);
  const coursePickerRef = useRef(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  useEffect(() => {
    if (!showCoursePicker) return undefined;
    const closeOutside = (event) => {
      if (!coursePickerRef.current?.contains(event.target)) setShowCoursePicker(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShowCoursePicker(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showCoursePicker]);
  const controls = useAnalyticsControls({
    storageKey: 'enquiries',
    chartKeys: ['overview', 'courseComparison', 'sources', 'status', 'courses', 'followups'],
    defaultPreset: 'month',
    defaultGroupBy: 'day',
  });

  const handlePeriodChange = useCallback((preset) => {
    controls.setPreset(preset);
    controls.setGroupBy(preset === 'year' ? 'month' : 'day');
  }, [controls]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const previousParams = buildPreviousQueryParams(controls.queryParams);
      const [currentRes, previousRes] = await Promise.all([
        api.get('/training/analytics/enquiries', { params: controls.queryParams }),
        api.get('/training/analytics/enquiries', { params: previousParams }),
      ]);
      setData(currentRes.data?.data || {});
      setPreviousData(previousRes.data?.data || {});
    } catch (_) {
      setData({});
      setPreviousData({});
    } finally {
      setLoading(false);
    }
  }, [controls.queryParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/training/courses', { params: { status: 'active', limit: 500 } })
      .then((r) => {
        const rows = Array.isArray(r.data?.data)
          ? r.data.data
          : Array.isArray(r.data?.records)
            ? r.data.records
            : [];
        setAllCourses(rows);
      })
      .catch(() => setAllCourses([]));
  }, []);

  const currentData = data || {};
  const previousAnalytics = previousData || {};

  const kpis = currentData.kpis || {};
  const trend = currentData.monthlyTrend || [];
  const previousTrend = previousAnalytics.monthlyTrend || [];
  const sourceSlices = currentData.sourceDistribution || [];
  const statusSlices = currentData.statusDistribution || [];
  const courseDist = useMemo(() => currentData.courseDistribution || [], [currentData.courseDistribution]);
  const previousCourseDist = useMemo(() => previousAnalytics.courseDistribution || [], [previousAnalytics.courseDistribution]);
  const courseSeries = useMemo(() => currentData.courseTrendSeries || [], [currentData.courseTrendSeries]);
  const previousCourseSeries = useMemo(() => previousAnalytics.courseTrendSeries || [], [previousAnalytics.courseTrendSeries]);
  const followUpSlices = currentData.followUpHealth || [];
  const meta = currentData.meta || {};
  const previousMeta = previousAnalytics.meta || {};
  const visible = controls.state.chartVisibility || {};
  const comparisonMeta = buildComparisonMeta(meta, previousMeta);
  const chartOptions = [
    { key: 'overview', label: 'Enquiry Overview', help: 'Compare total enquiries for the selected period and its previous equivalent period' },
    { key: 'courseComparison', label: 'Course Comparison', help: 'Compare enquiry interest by course' },
    { key: 'sources', label: 'Lead Sources', help: 'Where enquiries come from' },
    { key: 'status', label: 'Status Mix', help: 'Pipeline distribution' },
    { key: 'courses', label: 'Course Interest', help: 'Top courses by enquiry count' },
    { key: 'followups', label: 'Follow-up Health', help: 'Due, overdue, no follow-up, converted' },
  ];

  const courseOptions = useMemo(() => {
    const map = new Map();
    allCourses.forEach((item) => {
      const key = String(item._id || item.courseId || '');
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, {
          value: key,
          label: item.courseName || item.title || 'Course',
        });
      }
    });
    [...courseSeries, ...previousCourseSeries].forEach((item) => {
      const key = String(item.courseId || item.courseName || '');
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, {
          value: key,
          label: item.courseName || 'Course',
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [allCourses, courseSeries, previousCourseSeries]);

  const activeCourseIds = useMemo(() => {
    const ids = [
      ...courseSeries.filter((item) => Number(item.total || 0) > 0).map((item) => String(item.courseId || item.courseName || '')),
      ...previousCourseSeries.filter((item) => Number(item.total || 0) > 0).map((item) => String(item.courseId || item.courseName || '')),
    ].filter(Boolean);
    return Array.from(new Set(ids));
  }, [courseSeries, previousCourseSeries]);

  const comparisonCourseOptions = useMemo(() => (
    courseOptions.filter((option) => activeCourseIds.includes(option.value))
  ), [courseOptions, activeCourseIds]);

  useEffect(() => {
    setSelectedCourses((current) => {
      const valid = current.filter((value) => activeCourseIds.includes(value));
      const next = valid;
      const same = next.length === current.length && next.every((value, index) => value === current[index]);
      return same ? current : next;
    });
  }, [activeCourseIds]);

  const selectedComparison = useMemo(() => {
    const currentMap = new Map(courseSeries.map((item) => [String(item.courseId || item.courseName || ''), item]));
    const previousMap = new Map(previousCourseSeries.map((item) => [String(item.courseId || item.courseName || ''), item]));
    const optionMap = new Map(courseOptions.map((option) => [option.value, option]));
    return selectedCourses
      .map((value) => {
        const present = currentMap.get(value);
        const previous = previousMap.get(value);
        const selectedOption = optionMap.get(value);
        return {
          key: value,
          courseName: selectedOption?.label || present?.courseName || previous?.courseName || 'Course',
          present: Number(present?.total || 0),
          previous: Number(previous?.total || 0),
        };
      })
      .filter((item) => item.courseName);
  }, [selectedCourses, courseSeries, previousCourseSeries, courseOptions]);

  const previousLabel = previousTrend.length ? comparisonMeta.previousLabel : 'Previous period';
  const presentLabel = trend.length ? comparisonMeta.presentLabel : 'Present period';
  const presentTotal = Number(kpis.total || 0);
  const previousTotal = Number(previousAnalytics.kpis?.total || 0);
  const totalDifference = presentTotal - previousTotal;
  const totalDifferencePercent = previousTotal > 0
    ? Math.round((totalDifference / previousTotal) * 100)
    : (presentTotal > 0 ? 100 : 0);
  const overviewLabels = (trend.length >= previousTrend.length ? trend : previousTrend).map((item) => item.label);
  const singleDayComparison = trend.length <= 1 && previousTrend.length <= 1;
  const overviewDatasets = [
    {
      label: 'Present period',
      data: trend.map((item) => Number(item.count || 0)),
      color: '#2563eb',
      fill: true,
      pointRadius: 3,
    },
    {
      label: 'Previous equivalent period',
      data: previousTrend.map((item) => Number(item.count || 0)),
      color: '#f97316',
      fill: false,
      pointRadius: 3,
    },
  ];
  const chartComparison = selectedComparison;
  const courseTrendHeight = Math.min(460, 240 + Math.max(0, chartComparison.length - 4) * 16);
  const currentSeriesMap = useMemo(() => new Map(courseSeries.map((item) => [String(item.courseId || item.courseName || ''), item])), [courseSeries]);
  const previousSeriesMap = useMemo(() => new Map(previousCourseSeries.map((item) => [String(item.courseId || item.courseName || ''), item])), [previousCourseSeries]);
  const presentTrendLabels = useMemo(() => {
    const firstSeries = chartComparison
      .map((item) => currentSeriesMap.get(String(item.key)))
      .find((series) => Array.isArray(series?.points) && series.points.length);
    return (firstSeries?.points || []).map((point) => point.label);
  }, [chartComparison, currentSeriesMap]);
  const previousTrendLabels = useMemo(() => {
    const firstSeries = chartComparison
      .map((item) => previousSeriesMap.get(String(item.key)))
      .find((series) => Array.isArray(series?.points) && series.points.length);
    return (firstSeries?.points || []).map((point) => point.label);
  }, [chartComparison, previousSeriesMap]);
  const presentTrendDatasets = useMemo(() => (
    chartComparison
      .map((item, index) => {
        const series = currentSeriesMap.get(String(item.key));
        if (!series?.points?.length) return null;
        return {
          label: item.courseName,
          data: series.points.map((point) => Number(point.value || 0)),
          color: series.color || ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9', '#ec4899', '#84cc16'][index % 8],
          fill: false,
          pointRadius: 3,
        };
      })
      .filter(Boolean)
  ), [chartComparison, currentSeriesMap]);
  const previousTrendDatasets = useMemo(() => (
    chartComparison
      .map((item, index) => {
        const series = previousSeriesMap.get(String(item.key));
        if (!series?.points?.length) return null;
        return {
          label: item.courseName,
          data: series.points.map((point) => Number(point.value || 0)),
          color: series.color || ['#94a3b8', '#64748b', '#cbd5e1', '#475569', '#a1a1aa', '#9ca3af', '#71717a', '#52525b'][index % 8],
          fill: false,
          pointRadius: 3,
        };
      })
      .filter(Boolean)
  ), [chartComparison, previousSeriesMap]);

  if (loading && !data) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div>
      <AnalyticsToolbar
        title="Enquiry Comparison"
        subtitle="Choose a quick period or use From and To for a custom range."
        state={controls.state}
        meta={meta}
        presets={ENQUIRY_PRESETS}
        showTitle
        showGroupBy={false}
        dateInputLabels
        settingsLabel="Show / Hide Charts"
        infoLabel="How It Works"
        wrapPresets
        onPresetChange={handlePeriodChange}
        onGroupByChange={controls.setGroupBy}
        onFromChange={controls.setFrom}
        onToChange={controls.setTo}
        onOpenSettings={() => setShowSettings(true)}
        onOpenInfo={() => setShowInfo(true)}
      />

      <div className="row g-3 mb-4">
        {visible.overview !== false && (
          <div className="col-12">
            <ChartCard title="Enquiries: Present vs Previous" subtitle={`${presentLabel} compared with ${previousLabel}`} icon="bi bi-arrow-left-right" iconColor="#2563eb" onClick={() => navigate('/enquiries')}>
              <div className="row g-2 mb-3">
                <div className="col-12 col-md-4">
                  <div className="border rounded-3 px-3 py-2 h-100" style={{ background: 'rgba(37,99,235,0.06)', borderColor: 'rgba(37,99,235,0.16)' }}>
                    <div className="small text-muted">Selected period</div>
                    <div className="fs-4 fw-bold text-primary">{presentTotal}</div>
                    <div className="small text-muted text-truncate" title={presentLabel}>{presentLabel}</div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="border rounded-3 px-3 py-2 h-100" style={{ background: 'rgba(148,163,184,0.08)', borderColor: 'rgba(148,163,184,0.22)' }}>
                    <div className="small text-muted">Previous period</div>
                    <div className="fs-4 fw-bold text-dark">{previousTotal}</div>
                    <div className="small text-muted text-truncate" title={previousLabel}>{previousLabel}</div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="border rounded-3 px-3 py-2 h-100" style={{ background: totalDifference >= 0 ? 'rgba(34,197,94,0.07)' : 'rgba(239,68,68,0.07)', borderColor: totalDifference >= 0 ? 'rgba(34,197,94,0.20)' : 'rgba(239,68,68,0.20)' }}>
                    <div className="small text-muted">Change</div>
                    <div className={`fs-4 fw-bold ${totalDifference >= 0 ? 'text-success' : 'text-danger'}`}>{totalDifference >= 0 ? '+' : ''}{totalDifference}</div>
                    <div className={`small ${totalDifference >= 0 ? 'text-success' : 'text-danger'}`}>{totalDifference >= 0 ? 'Up' : 'Down'} {Math.abs(totalDifferencePercent)}% from previous</div>
                  </div>
                </div>
              </div>
              {singleDayComparison ? <BarChart
                height={280}
                labels={['Daily enquiries']}
                datasets={[
                  { label: presentLabel, data: [presentTotal], color: '#2563eb' },
                  { label: previousLabel, data: [previousTotal], color: '#f97316' },
                ]}
                themeColor="#2563eb"
                yMin={0}
              /> : <LineChart
                height={280}
                labels={overviewLabels}
                datasets={overviewDatasets}
                themeColor="#2563eb"
                yMin={0}
                xAxisTitle="Selected-period dates"
                yAxisTitle="Enquiries"
              />}
            </ChartCard>
          </div>
        )}
      </div>

      <div className="row g-3 mb-4">
        {visible.courseComparison !== false && (
          <div className="col-12">
            <ChartCard title="Course Performance Comparison" subtitle={`${presentLabel} compared with ${previousLabel}`} icon="bi bi-bar-chart-line-fill" iconColor="#f59e0b" onClick={() => navigate('/enquiries')}>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <span className="badge text-bg-primary">{presentLabel}</span>
                    <span className="badge text-bg-light border">{previousLabel}</span>
                  </div>
                  <div className="w-100 position-relative" ref={coursePickerRef} onClick={(event) => event.stopPropagation()}>
                    <label className="form-label form-label-sm mb-1 fw-semibold">Select Courses to Compare</label>
                    <div className="small text-muted mb-2">Only courses with enquiry activity in these two compared periods are listed.</div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between"
                      style={{ width: '100%', maxWidth: 320, minWidth: 0 }}
                      onClick={() => setShowCoursePicker((current) => !current)}
                    >
                      <span>{selectedCourses.length ? `${selectedCourses.length} course${selectedCourses.length > 1 ? 's' : ''} selected` : 'Select courses'}</span>
                      <i className={`bi ${showCoursePicker ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                    </button>
                    {showCoursePicker && (
                      <div className="position-absolute mt-2 p-3 border rounded-3 bg-white shadow-sm" style={{ zIndex: 20, width: 'min(100%, 320px)', maxHeight: 280, overflowY: 'auto' }}>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <button type="button" className="btn btn-light btn-sm border" onClick={() => { setSelectedCourses(comparisonCourseOptions.slice(0, 6).map((option) => option.value)); setShowCoursePicker(false); }}>Top 6</button>
                          <button type="button" className="btn btn-light btn-sm border" onClick={() => { setSelectedCourses(comparisonCourseOptions.map((option) => option.value)); setShowCoursePicker(false); }}>Select all</button>
                          <button type="button" className="btn btn-light btn-sm border" onClick={() => { setSelectedCourses([]); setShowCoursePicker(false); }}>Clear</button>
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {comparisonCourseOptions.map((option) => (
                            <label key={option.value} className="d-flex align-items-center gap-2 small">
                              <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={selectedCourses.includes(option.value)}
                                onChange={(e) => {
                                  setSelectedCourses((current) => (
                                    e.target.checked
                                      ? [...current, option.value]
                                      : current.filter((value) => value !== option.value)
                                  ));
                                  setShowCoursePicker(false);
                                }}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {selectedCourses.map((value) => {
                        const option = courseOptions.find((entry) => entry.value === value);
                        return option ? <span key={value} className="badge rounded-pill text-bg-light border text-dark">{option.label}</span> : null;
                      })}
                    </div>
                  </div>
                </div>

                {selectedComparison.length ? (
                  <>
                    <>
                      <div className="row g-3">
                        <div className="col-12">
                          <ChartCard
                            title="Present Trend"
                            subtitle={presentLabel}
                            icon="bi bi-graph-up-arrow"
                            iconColor="#3b82f6"
                            onClick={() => navigate('/enquiries')}
                          >
                            <LineChart
                              height={courseTrendHeight}
                              labels={presentTrendLabels}
                              datasets={presentTrendDatasets}
                              themeColor="#3b82f6"
                              yMin={0}
                            />
                          </ChartCard>
                        </div>
                        <div className="col-12">
                          <ChartCard
                            title="Previous Trend"
                            subtitle={previousLabel}
                            icon="bi bi-graph-up"
                            iconColor="#94a3b8"
                            onClick={() => navigate('/enquiries')}
                          >
                            <LineChart
                              height={courseTrendHeight}
                              labels={previousTrendLabels}
                              datasets={previousTrendDatasets}
                              themeColor="#94a3b8"
                              yMin={0}
                            />
                          </ChartCard>
                        </div>
                      </div>
                    </>
                    <div className="row g-2">
                      {selectedComparison.map((item) => (
                        <div className="col-12 col-sm-6 col-xl-4" key={item.key}>
                          <div className="border rounded-3 px-3 py-2 h-100">
                            <div className="fw-semibold text-truncate" title={item.courseName}>{item.courseName}</div>
                            <div className="d-flex align-items-center justify-content-between gap-2 mt-2 small">
                              <span className="text-primary">Present <strong>{item.present}</strong></span>
                              <span style={{ color: '#ea580c' }}>Previous <strong>{item.previous}</strong></span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <EmptyChart label="course comparison" themeColor="#3b82f6" />
                )}
              </div>
            </ChartCard>
          </div>
        )}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-people-fill" label="Total Enquiries" value={kpis.total} loading={loading} color="#3b82f6" sub="filtered period" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-person-plus-fill" label="New in Range" value={kpis.newThisMonth} loading={loading} color="#22c55e" sub="current filter" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-telephone-fill" label="In Follow-up" value={kpis.following} loading={loading} color="#f59e0b" sub="needs contact" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-person-check-fill" label="Converted" value={kpis.enrolled} loading={loading} color="#10b981" sub="joined students" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-alarm-fill" label="Due Soon" value={kpis.dueSoon} loading={loading} color="#ef4444" sub="next 3 days" /></div>
        <div className="col-6 col-md-4 col-lg-2"><AnimatedKpiCard icon="bi-graph-up-arrow" label="Conversion %" value={`${kpis.conversionRate || 0}%`} loading={loading} color="#8b5cf6" sub="enquiry to join" /></div>
      </div>

      <div className="row g-3 mb-4">
        {visible.sources !== false && <div className="col-lg-4"><ChartCard title="Lead Sources" subtitle={meta.periodLabel || 'Where enquiries come from'} icon="bi-pie-chart-fill" iconColor="#8b5cf6" onClick={() => navigate('/enquiries')}>{sourceSlices.length ? <DoughnutChart height={280} slices={sourceSlices} centerLabel={sourceSlices.reduce((sum, slice) => sum + slice.value, 0)} centerSubLabel="Leads" themeColor="#8b5cf6" /> : <EmptyChart label="lead sources" themeColor="#8b5cf6" />}</ChartCard></div>}
      </div>

      <div className="row g-3 mb-4">
        {visible.status !== false && <div className="col-lg-5"><ChartCard title="Status Mix" subtitle={meta.periodLabel || 'Current pipeline distribution'} icon="bi-diagram-3-fill" iconColor="#0ea5e9" onClick={() => navigate('/enquiries')}>{statusSlices.length ? <DoughnutChart height={280} slices={statusSlices} centerLabel={statusSlices.reduce((sum, slice) => sum + slice.value, 0)} centerSubLabel="Open" themeColor="#0ea5e9" /> : <EmptyChart label="status mix" themeColor="#0ea5e9" />}</ChartCard></div>}
        {visible.courses !== false && <div className="col-lg-7"><ChartCard title="Course Interest" subtitle={meta.periodLabel || 'Top courses by enquiry count'} icon="bi-bar-chart-line-fill" iconColor="#f59e0b" onClick={() => navigate('/enquiries')}>{courseDist.length ? <BarChart height={280} labels={courseDist.map((item) => (item.courseName || '').length > 20 ? `${item.courseName.slice(0, 19)}...` : item.courseName)} datasets={[{ label: 'Enquiries', data: courseDist.map((item) => item.count), color: '#f59e0b' }]} themeColor="#f59e0b" onBarClick={({ index }) => { const item = courseDist[index]; if (item?.courseId) navigate(`/enquiries?course=${encodeURIComponent(item.courseId)}`); }} /> : <EmptyChart label="course interest" themeColor="#f59e0b" />}</ChartCard></div>}
      </div>
      <div className="row g-3">
        {visible.followups !== false && <div className="col-lg-4"><ChartCard title="Follow-up Health" subtitle={meta.periodLabel || 'Actionability of current enquiries'} icon="bi-activity" iconColor="#ef4444" onClick={() => navigate('/enquiries?dueSoon=true')}>{followUpSlices.length ? <DoughnutChart height={260} slices={followUpSlices} centerLabel={followUpSlices.reduce((sum, slice) => sum + slice.value, 0)} centerSubLabel="Tracked" themeColor="#ef4444" /> : <EmptyChart label="follow-up health" themeColor="#ef4444" />}</ChartCard></div>}
      </div>

      <AnalyticsSettingsModal show={showSettings} charts={chartOptions} visibility={visible} onToggle={controls.toggleChart} onReset={controls.reset} onClose={() => setShowSettings(false)} />
      <AnalyticsInfoModal show={showInfo} title="Enquiry Analytics Guide" lines={['The first chart shows total enquiries for the selected period against the equivalent previous period.', 'Today compares with yesterday. This week compares with the same elapsed days of last week. This month compares with the same elapsed dates of last month.', 'Use Course Performance Comparison below when you need a course-by-course view.']} onClose={() => setShowInfo(false)} />
    </div>
  );
}
